import React, { useMemo } from 'react'
import { Pressable, Text, View } from 'react-native'
import { ALL_SKILL_LEVELS, type BotSkillLevel } from '@ppwr/poker-engine/utils'
import { BOT_SKILL_COLORS, BOT_SKILL_LABELS } from '../../../utils/botSkillColors'
import { teacherControlStyles as styles } from './styles'
import type { BotSkillCounts } from './types'

export type AddBotsContentProps = {
  availableSeats: number
  isAddingDuringGame: boolean
  skillCounts: BotSkillCounts
  isSubmitting?: boolean
  onClose: () => void
  onConfirm: () => void
  onIncrement: (skillLevel: BotSkillLevel) => void
  onDecrement: (skillLevel: BotSkillLevel) => void
}

export function AddBotsContent({
  availableSeats,
  isAddingDuringGame,
  skillCounts,
  isSubmitting = false,
  onClose,
  onConfirm,
  onIncrement,
  onDecrement
}: AddBotsContentProps) {
  const totalSelected = useMemo(
    () => ALL_SKILL_LEVELS.reduce((sum, skillLevel) => sum + skillCounts[skillLevel], 0),
    [skillCounts]
  )
  const canConfirm = totalSelected > 0 && !isSubmitting
  const atCapacity = totalSelected >= availableSeats

  return (
    <View>
      <View style={styles.sheetHeader}>
        <Text style={styles.sheetTitle}>Add Bots</Text>
        <Text style={styles.sheetSubtitle}>
          {availableSeats} open seat{availableSeats === 1 ? '' : 's'}
        </Text>
        {isAddingDuringGame && <Text style={styles.sheetHint}>Bots added during play join next hand.</Text>}
      </View>

      <View style={[styles.skillRows, { marginTop: 16 }]}>
        {ALL_SKILL_LEVELS.map((skillLevel) => {
          const count = skillCounts[skillLevel]
          const disableDecrement = count === 0 || isSubmitting
          const disableIncrement = atCapacity || isSubmitting

          return (
            <View key={skillLevel} style={styles.skillRow}>
              <View style={styles.skillInfo}>
                <View style={[styles.skillSwatch, { backgroundColor: BOT_SKILL_COLORS[skillLevel] }]} />
                <Text style={styles.skillLabel}>{BOT_SKILL_LABELS[skillLevel]}</Text>
              </View>

              <View style={styles.skillStepper}>
                <Pressable
                  onPress={() => onDecrement(skillLevel)}
                  disabled={disableDecrement}
                  style={({ pressed }) => [
                    styles.skillStepperButton,
                    disableDecrement && styles.skillStepperButtonDisabled,
                    pressed && !disableDecrement && styles.skillStepperButtonPressed
                  ]}
                >
                  <Text style={[styles.skillStepperButtonText, disableDecrement && styles.skillStepperButtonTextDisabled]}>−</Text>
                </Pressable>

                <View style={styles.skillStepperCount}>
                  <Text style={styles.skillStepperCountText}>{count}</Text>
                </View>

                <Pressable
                  onPress={() => onIncrement(skillLevel)}
                  disabled={disableIncrement}
                  style={({ pressed }) => [
                    styles.skillStepperButton,
                    disableIncrement && styles.skillStepperButtonDisabled,
                    pressed && !disableIncrement && styles.skillStepperButtonPressed
                  ]}
                >
                  <Text style={[styles.skillStepperButtonText, disableIncrement && styles.skillStepperButtonTextDisabled]}>+</Text>
                </Pressable>
              </View>
            </View>
          )
        })}
      </View>

      <View style={[styles.sheetFooter, { marginTop: 16 }]}>
        <Text style={styles.sheetFooterText}>
          Adding {totalSelected} bot{totalSelected === 1 ? '' : 's'}
        </Text>

        <View style={styles.sheetActions}>
          <Pressable
            onPress={onClose}
            disabled={isSubmitting}
            style={({ pressed }) => [
              styles.sheetSecondaryButton,
              isSubmitting && styles.sheetSecondaryButtonDisabled,
              pressed && !isSubmitting && { opacity: 0.7 }
            ]}
          >
            <Text style={styles.sheetSecondaryButtonText}>Cancel</Text>
          </Pressable>
          <Pressable
            onPress={onConfirm}
            disabled={!canConfirm}
            style={({ pressed }) => [
              styles.sheetPrimaryButton,
              !canConfirm && styles.sheetPrimaryButtonDisabled,
              pressed && canConfirm && { opacity: 0.85 }
            ]}
          >
            <Text style={[styles.sheetPrimaryButtonText, !canConfirm && styles.sheetPrimaryButtonTextDisabled]}>
              Add {totalSelected} Bot{totalSelected === 1 ? '' : 's'}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  )
}
